/**
 * WorldPulseHome — universal home template for the geographic
 * platform + its country-branded forks.
 *
 * One component, three brands:
 *   • worldpulse.pro  — world grid: pick a country, drill down
 *   • indias.com      — India-only home (all subdivisions surfaced)
 *   • usapulse.net    — USA-only home (states surfaced first)
 *
 * The brand is decided by `siteProfile(slug).countryIso`. If set, we
 * load the country's portal-home directly. Otherwise (worldpulse) we
 * show the country picker.
 *
 * Reads the existing `locations` collection through /api/wp/*.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Globe2, MapPin, Building2, Loader2, Cloud, Newspaper, AlertTriangle, ExternalLink } from "lucide-react";

import { API } from "@/lib/api";
import { resolveSiteSlug, siteProfile } from "@/config/siteConfig";
import { useContextualPhoto, UnsplashCredit } from "@/hooks/useContextualPhoto";

export default function WorldPulseHome() {
  const [params] = useSearchParams();
  const queryParam = (params.get("site") || "").toLowerCase();
  const slug = queryParam || resolveSiteSlug();
  const profile = siteProfile(slug);
  const iso = profile.countryIso;    // "IN" / "US" / undefined
  const accent = profile.accent || "#4f46e5";

  const [countryHome, setCountryHome] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [searchHits, setSearchHits] = useState([]);
  const heroPhoto = useContextualPhoto(
    profile.slug === "indias" ? "India landscape mountains temple"
    : profile.slug === "usapulse" ? "United States landscape mountains national park"
    : "world map continents earth"
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (iso) {
          const r = await axios.get(`${API}/wp/portal-home/${iso}`);
          if (!cancelled) setCountryHome(r.data);
        } else {
          const r = await axios.get(`${API}/wp/countries`);
          if (!cancelled) setCountries(r.data.countries || []);
        }
      } catch (e) {
        // Fail soft — surface a "coming soon" state below.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [iso]);

  // Debounced live search across the geographic knowledge graph.
  useEffect(() => {
    if (!q || q.length < 2) { setSearchHits([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { q, limit: 8 };
        if (iso) params.country = iso;
        const r = await axios.get(`${API}/wp/locations/search`, { params });
        setSearchHits(r.data.hits || []);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [q, iso]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="worldpulse-home">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent} 0%, ${accent}dd 100%)` }}>
        {heroPhoto?.url && (
          <>
            <div className="absolute inset-0" style={{
              backgroundImage: `url('${heroPhoto.url}')`,
              backgroundSize: "cover", backgroundPosition: "center",
              opacity: 0.35,
            }} />
            <div className="absolute inset-0" style={{
              background: `linear-gradient(135deg, ${accent}ee 0%, ${accent}99 100%)`,
            }} />
          </>
        )}
        <div className="relative max-w-6xl mx-auto px-4 py-14 sm:py-20 text-white">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider opacity-80 mb-3">
            <Globe2 className="w-4 h-4" />
            {iso ? `${countryHome?.portal?.portal_name || profile.label} · a WorldPulse country portal` : "WorldPulse · Global Geographic Portal"}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-4 tracking-tight">
            {iso
              ? countryHome?.country?.official_name
              : "The world's places, people, and community."}
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mb-8">
            {iso
              ? `Explore ${countryHome?.subdivisions_total || 0} ${(countryHome?.admin_levels?.[0]?.label_plural || 'regions').toLowerCase()}, districts, cities, and villages. One profile across every SocialPulse sister site.`
              : "Countries, states, districts, cities, and villages — one canonical geographic knowledge graph, three-hundred fifty branded experiences and counting."}
          </p>

          {/* Universal search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={iso ? "Search states, districts, cities…" : "Search countries, states, districts, cities…"}
              className="w-full pl-11 pr-4 py-3.5 rounded-lg bg-white/95 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-white/40 shadow-lg"
              data-testid="wp-search-input"
            />
            {searchHits.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-xl overflow-hidden z-10">
                {searchHits.map((h) => (
                  <Link
                    key={h.id}
                    to={`/wp/${h.slug || h.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 border-b last:border-b-0"
                    onClick={() => setQ("")}
                    data-testid={`wp-search-hit-${h.id}`}
                  >
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{h.official_name}</div>
                      <div className="text-xs text-slate-500 truncate">{h.path || h.kind}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{h.kind}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        {iso ? <CountryPortalBody home={countryHome} accent={accent} /> : <WorldGrid countries={countries} accent={accent} />}
      </div>

      {/* Footer strip — sister-app family */}
      <div className="border-t bg-white">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-slate-500 flex flex-wrap gap-4">
          <span className="uppercase tracking-wider text-slate-400">Part of the SocialPulse family</span>
          <a href="https://socialpulse.com" className="hover:text-slate-900">SocialPulse</a>
          <a href="https://indias.com" className="hover:text-slate-900">Indias.com</a>
          <a href="https://usapulse.net" className="hover:text-slate-900">USAPulse</a>
          <a href="https://foodpulse.net" className="hover:text-slate-900">FoodPulse</a>
          <a href="https://naturepulse.net" className="hover:text-slate-900">NaturePulse</a>
          <a href="https://travelerpulse.pro" className="hover:text-slate-900">TravelerPulse</a>
          <a href="https://alumnipulse.net" className="hover:text-slate-900">AlumniPulse</a>
          <a href="https://sellnbuy.com" className="hover:text-slate-900">SellnBuy</a>
        </div>
      </div>
    </div>
  );
}


// ─── Country-scoped body (Indias.com / USAPulse.net) ────────────────


function CountryPortalBody({ home, accent }) {
  if (!home) return null;
  const subs = home.subdivisions || [];
  const level1Label = home.admin_levels?.[0]?.label_plural || "Regions";
  const level2Label = home.admin_levels?.[1]?.label_plural || "Districts";

  return (
    <>
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatCard label={level1Label} value={home.subdivisions_total} accent={accent} icon={Building2} testid="stat-subdivisions" />
        <StatCard label="Country ISO" value={home.country?.iso_country_code || "—"} accent={accent} icon={Globe2} testid="stat-iso" />
        <StatCard label="Portal domain" value={home.portal?.primary_domain || "—"} accent={accent} icon={MapPin} testid="stat-domain" />
      </div>

      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-2xl font-semibold text-slate-900">{level1Label}</h2>
          <span className="text-sm text-slate-500">{subs.length} total</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="wp-subdivisions-grid">
          {subs.map((s) => (
            <Link
              key={s.id}
              to={`/wp/${s.slug || s.id}`}
              className="group border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
              style={{ borderLeftWidth: 3, borderLeftColor: accent }}
              data-testid={`wp-subdivision-${s.id}`}
            >
              <div className="text-sm font-medium text-slate-900 group-hover:underline truncate">{s.official_name}</div>
              <div className="text-xs text-slate-500 mt-1">{s.iso_subdivision_code || home.admin_levels?.[0]?.label_singular || "region"}</div>
            </Link>
          ))}
        </div>
        {subs.length === 0 && (
          <div className="text-center py-16 text-slate-400 border-2 border-dashed rounded-lg">
            No subdivisions found. Data seeding in progress.
          </div>
        )}
      </section>

      {/* Phase 3 live tiles — Weather, News, Alerts */}
      <section className="mt-14 grid lg:grid-cols-3 gap-4">
        <WeatherTile weather={home.weather} accent={accent} />
        <AlertsTile alerts={home.alerts} accent={accent} />
        <div className="lg:col-span-3">
          <NewsTile news={home.news} accent={accent} />
        </div>
      </section>

      {/* Phase 4+ placeholders */}
      <section className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[
          ["Events", "Phase 4", "Community + government + partner feeds."],
          ["Community", "Phase 4", "Entity-linked posts, photos, stories."],
          ["Businesses", "Phase 5", "Local directory + claims + advertising."],
        ].map(([title, phase, desc]) => (
          <div key={title} className="border rounded-lg p-4 bg-white opacity-60" data-testid={`wp-tile-${title.toLowerCase()}`}>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{phase}</div>
            <div className="text-base font-medium text-slate-900 mb-1">{title}</div>
            <div className="text-sm text-slate-500">{desc}</div>
          </div>
        ))}
      </section>
    </>
  );
}


// ─── Phase-3 tiles ──────────────────────────────────────────────────


function WeatherTile({ weather, accent }) {
  if (!weather) {
    return (
      <div className="bg-white border rounded-lg p-5 opacity-60" data-testid="wp-tile-weather">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 mb-2">
          <Cloud className="w-3.5 h-3.5" /> Weather
        </div>
        <div className="text-sm text-slate-500">Not yet available for this location.</div>
      </div>
    );
  }
  const c = weather.current || {};
  return (
    <div className="bg-white border rounded-lg p-5" data-testid="wp-tile-weather" style={{ borderTopWidth: 3, borderTopColor: accent }}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-2">
        <Cloud className="w-3.5 h-3.5" style={{ color: accent }} /> Weather · <span className="text-slate-400">{weather.provider}</span>
      </div>
      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-4xl font-semibold text-slate-900 tabular-nums">{Math.round(c.temp_c)}°C</div>
        <div className="text-sm text-slate-600">{c.condition}</div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
        {c.feels_like_c != null && <div>Feels like <span className="text-slate-800">{Math.round(c.feels_like_c)}°C</span></div>}
        {c.humidity_pct != null && <div>Humidity <span className="text-slate-800">{c.humidity_pct}%</span></div>}
        {c.wind_speed_kmh != null && <div>Wind <span className="text-slate-800">{Math.round(c.wind_speed_kmh)} km/h</span></div>}
        {c.precipitation_mm != null && <div>Rain <span className="text-slate-800">{c.precipitation_mm} mm</span></div>}
      </div>
      {weather.forecast_days?.length > 0 && (
        <div className="mt-3 pt-3 border-t grid grid-cols-7 gap-1 text-center text-[10px] text-slate-500">
          {weather.forecast_days.slice(0, 7).map((d, i) => (
            <div key={i}>
              <div className="uppercase tracking-wider text-slate-400">{(d.date || "").slice(5)}</div>
              <div className="text-slate-800 font-medium mt-0.5">{Math.round(d.temp_max_c)}°</div>
              <div className="text-slate-400">{Math.round(d.temp_min_c)}°</div>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-slate-400 mt-3">{weather.attribution}</div>
    </div>
  );
}


function AlertsTile({ alerts, accent }) {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-5 opacity-60" data-testid="wp-tile-alerts">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400 mb-2">
          <AlertTriangle className="w-3.5 h-3.5" /> Alerts
        </div>
        <div className="text-sm text-slate-500">No active official alerts.</div>
      </div>
    );
  }
  const sevColour = (s) => ({
    extreme: "#dc2626", severe: "#ea580c", moderate: "#d97706", minor: "#65a30d"
  }[s] || "#6b7280");
  return (
    <div className="bg-white border rounded-lg p-5" data-testid="wp-tile-alerts" style={{ borderTopWidth: 3, borderTopColor: "#dc2626" }}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-3">
        <AlertTriangle className="w-3.5 h-3.5 text-red-600" /> Official Alerts · <span className="text-slate-400">{alerts.length}</span>
      </div>
      <div className="space-y-2">
        {alerts.slice(0, 4).map((a, i) => (
          <div key={a.id || i} className="border-l-2 pl-2 py-0.5" style={{ borderColor: sevColour(a.severity) }} data-testid={`wp-alert-${i}`}>
            <div className="text-sm text-slate-900 font-medium truncate">{a.event || a.title}</div>
            <div className="text-xs text-slate-500 truncate">
              <span style={{ color: sevColour(a.severity) }}>{a.severity || "—"}</span>
              {a.area && <> · {a.area.split(";")[0]}</>}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-3">{alerts[0]?.attribution || "Official provider"}</div>
    </div>
  );
}


function NewsTile({ news, accent }) {
  if (!news || news.length === 0) return null;
  return (
    <div className="bg-white border rounded-lg p-5" data-testid="wp-tile-news">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-3">
        <Newspaper className="w-3.5 h-3.5" style={{ color: accent }} /> Latest news · <span className="text-slate-400">{news.length}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {news.slice(0, 8).map((n, i) => (
          <a
            key={n.guid || i}
            href={n.original_url} target="_blank" rel="noreferrer"
            className="flex gap-3 p-2 rounded-md hover:bg-slate-50 border border-transparent hover:border-slate-200"
            data-testid={`wp-news-${i}`}
          >
            {n.image_url && (
              <img src={n.image_url} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" loading="lazy" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 leading-tight line-clamp-2">{n.headline}</div>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <span className="truncate">{n.source_name}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </div>
            </div>
          </a>
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-3">Headlines link out to original publishers. WorldPulse never republishes copyrighted content.</div>
    </div>
  );
}


// ─── World grid (worldpulse.pro root) ───────────────────────────────


function WorldGrid({ countries, accent }) {
  const grouped = useMemo(() => {
    const by = {};
    for (const c of countries) {
      const letter = (c.name || "?")[0].toUpperCase();
      (by[letter] ||= []).push(c);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [countries]);

  return (
    <>
      <div className="grid md:grid-cols-3 gap-4 mb-10">
        <StatCard label="Countries" value={countries.length} accent={accent} icon={Globe2} testid="stat-countries" />
        <StatCard label="Branded portals" value={2} accent={accent} icon={Building2} testid="stat-portals" />
        <StatCard label="Locations under management" value="4,995" accent={accent} icon={MapPin} testid="stat-locations" />
      </div>

      <div className="mb-10 bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Branded country portals</h3>
        <p className="text-sm text-slate-600 mb-4">Countries with a dedicated public domain, powered by the same WorldPulse engine.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <a href="https://indias.com" className="border rounded-lg p-4 hover:shadow-md transition" style={{ borderLeftWidth: 3, borderLeftColor: "#059669" }} data-testid="portal-indias">
            <div className="text-sm font-medium text-slate-900">Indias.com</div>
            <div className="text-xs text-slate-500 mt-1">India · 28 States/UTs · 785 districts · Hindi + 9 more languages</div>
          </a>
          <a href="https://usapulse.net" className="border rounded-lg p-4 hover:shadow-md transition" style={{ borderLeftWidth: 3, borderLeftColor: "#2563eb" }} data-testid="portal-usapulse">
            <div className="text-sm font-medium text-slate-900">USAPulse.net</div>
            <div className="text-xs text-slate-500 mt-1">United States · 50 States · 3,162 counties · English + Spanish</div>
          </a>
        </div>
      </div>

      <section>
        <h2 className="text-2xl font-semibold text-slate-900 mb-4">All countries</h2>
        <div className="space-y-6" data-testid="wp-country-grid">
          {grouped.map(([letter, list]) => (
            <div key={letter}>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">{letter}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {list.map((c) => (
                  <Link
                    key={c.id}
                    to={`/wp/${c.slug || c.id}`}
                    className="text-sm text-slate-700 hover:text-slate-900 hover:underline truncate py-1"
                    data-testid={`wp-country-${c.iso || c.id}`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}


// ─── Reusable ────────────────────────────────────────────────────────


function StatCard({ label, value, accent, icon: Icon, testid }) {
  return (
    <div className="bg-white border rounded-lg p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="text-3xl font-semibold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
